// src/features/permissions/PermissionsPage.tsx
import { useState, type FormEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { useRoles, useGrantRole, useRevokeRole, type RoleGrant } from './useRoles';
import './PermissionsPage.css';

// Mirrors roles.js's SNOWFLAKE_RE exactly, for immediate client-side
// feedback — the server remains the actual source of truth.
const SNOWFLAKE_RE = /^\d{17,20}$/;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function GrantForm({ guildId }: { guildId: string }) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'admin' | 'moderator'>('moderator');
  const grant = useGrantRole(guildId);
  const [justGranted, setJustGranted] = useState(false);

  const isValid = SNOWFLAKE_RE.test(userId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    // Deliberately NOT clearing userId here anymore — this used to
    // reset it to '', which SNOWFLAKE_RE.test('') fails, silently
    // re-disabling the button immediately after every successful
    // grant (same bug found and fixed in Market Alerts and Scheduled
    // Reports). Kept as-is so granting the same user a second role,
    // or granting several users in a row while only changing the
    // role dropdown, doesn't require retyping the ID each time.
    grant.mutate({ userId, role }, {
      onSuccess: () => {
        setJustGranted(true);
        setTimeout(() => setJustGranted(false), 2500);
      },
    });
  }

  return (
    <form className="permissions-form" onSubmit={handleSubmit}>
      <div className="permissions-form__field">
        <label htmlFor="userId">Discord user ID</label>
        <input
          id="userId"
          className="input"
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value.trim())}
          placeholder="e.g. 123456789012345678"
        />
        {userId.length > 0 && !isValid && (
          <p className="permissions-form__error">Must be a Discord snowflake — 17 to 20 digits.</p>
        )}
      </div>

      <div className="permissions-form__field">
        <label htmlFor="role">Role</label>
        <select
          id="role"
          className="input"
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'moderator')}
        >
          <option value="moderator">Moderator</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <button className="button-primary" type="submit" disabled={!isValid || grant.isPending}>
        {grant.isPending ? 'Granting…' : 'Grant role'}
      </button>
      {grant.isError && (
        <span style={{ color: 'var(--negative)', fontSize: 13, marginLeft: '1rem' }}>
          {grant.error instanceof Error ? grant.error.message : "Couldn't grant the role. Check the ID and try again."}
        </span>
      )}
      {justGranted && <span style={{ color: 'var(--positive)', fontSize: 13, marginLeft: '1rem' }}>Granted.</span>}
    </form>
  );
}

function RoleRow({ guildId, grant, index }: { guildId: string; grant: RoleGrant; index: number }) {
  const revoke = useRevokeRole(guildId);

  return (
    <tr className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
      <td className="permissions-table__id">{grant.user_id}</td>
      <td>
        <span className={`status-badge ${grant.role === 'admin' ? 'status-badge--warning' : 'status-badge--fresh'}`}>
          {grant.role === 'admin' ? 'Admin' : 'Moderator'}
        </span>
      </td>
      <td className="permissions-table__date">{formatDate(grant.added_at)}</td>
      <td>
        <button
          className="button-danger"
          onClick={() => revoke.mutate(grant.user_id)}
          disabled={revoke.isPending}
        >
          {revoke.isPending ? 'Removing…' : 'Revoke'}
        </button>
      </td>
    </tr>
  );
}

export function PermissionsPage() {
  const { selectedGuildId } = useGuild();
  const { data: roles, isLoading, error } = useRoles(selectedGuildId);

  const adminCount = roles?.filter((r) => r.role === 'admin').length ?? 0;
  const modCount = roles?.filter((r) => r.role === 'moderator').length ?? 0;

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Dashboard access</p>
        <h1 className="page-header__title">Permissions</h1>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 1rem' }}>
        {adminCount} / 5 admins · {modCount} / 5 moderators
      </p>

      <article className="card" style={{ marginBottom: '1.25rem' }}>
        <header className="card__header">
          <div>
            <p className="card__eyebrow">Grant access</p>
            <h2 className="card__title">Add an admin or moderator</h2>
          </div>
        </header>
        <div className="card__content">
          {selectedGuildId && <GrantForm guildId={selectedGuildId} />}
        </div>
      </article>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">{roles?.length ?? 0} granted</p>
            <h2 className="card__title">Current admins and moderators</h2>
          </div>
        </header>
        <div className="card__content">
          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load permissions. Try refreshing.</p>}
          {roles?.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No admins or moderators granted yet for this server.</p>
          )}
          {roles && roles.length > 0 && selectedGuildId && (
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Role</th>
                  <th>Granted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {roles.map((grant, index) => (
                  <RoleRow key={grant.id} guildId={selectedGuildId} grant={grant} index={index} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
